import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import AlertsPanel from '../components/AlertsPanel';
import AlertDetailModal from '../components/AlertDetailModal';
import NDVIVisualization from '../components/NDVIVisualization';
import ScoutingPanel from '../components/ScoutingPanel';
import TasksPanel, { createTaskFromAlert } from '../components/TasksPanel';
import AgronomistCaseButton from '../components/AgronomistCaseButton';
import { exportSeasonData } from '../services/exportService';
import { 
  MessageSquare, Activity, TrendingUp, Sprout, Trash2, AlertCircle, Leaf, Calendar, Plus, 
  CloudRain, FileText, RefreshCw, Loader2, MapPin, DollarSign, Download, Clipboard,
  Target, CheckSquare, Camera, Send, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Available crop types
const CROP_TYPES = [
  { value: 'wheat_winter', label: 'Winter Wheat' },
  { value: 'wheat_spring', label: 'Spring Wheat' },
  { value: 'barley_winter', label: 'Winter Barley' },
  { value: 'barley_spring', label: 'Spring Barley' },
  { value: 'corn', label: 'Corn/Maize' },
  { value: 'potatoes', label: 'Potatoes' },
  { value: 'rapeseed', label: 'Rapeseed/Canola' },
  { value: 'oats', label: 'Oats' },
  { value: 'peas', label: 'Peas' },
  { value: 'chickpeas', label: 'Chickpeas' },
  { value: 'lentils', label: 'Lentils' },
];

const FieldDetails = () => {
  const { fieldId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthHeaders } = useAuth();
  const [field, setField] = useState(null);
  const [activities, setActivities] = useState([]);
  const [ndviReadings, setNdviReadings] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatMessage, setChatMessage] = useState('');
  const [chatResponse, setChatResponse] = useState('');
  const [chatUsage, setChatUsage] = useState({ used: 0, limit: 20, remaining: 20 });
  const [scoutImage, setScoutImage] = useState(null);
  const [scoutImagePreview, setScoutImagePreview] = useState(null);
  const [showCropHealthScout, setShowCropHealthScout] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [showAlerts, setShowAlerts] = useState(true);
  const [showNDVI, setShowNDVI] = useState(false);
  const [showNewCropModal, setShowNewCropModal] = useState(false);
  const [showSummaries, setShowSummaries] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [dismissingAlert, setDismissingAlert] = useState(false);
  
  // New state for features 1-5
  const [activeTab, setActiveTab] = useState('overview'); // overview, scouting, tasks
  const [seasonCosts, setSeasonCosts] = useState({ totalCost: 0, costPerHa: 0, productBreakdown: {}, byCurrency: {} });
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [showExportModal, setShowExportModal] = useState(false);
  const [farmCurrency, setFarmCurrency] = useState('EUR'); // Default farm currency
  
  const [activityForm, setActivityForm] = useState({
    activity_type: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    // Cost tracking fields
    productName: '',
    quantityPerHa: '',
    quantityUnit: '',
    costPerHa: '',
    currency: 'EUR' // A) Currency selector - default EUR
  });
  const [newCropForm, setNewCropForm] = useState({
    crop: '',
    sowing_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadFieldData();
    loadChatUsage();
    loadSummaries();
    loadSeasonCosts();
    loadFarmCurrency();
  }, [fieldId]);

  // Handle openCase URL parameter (from Dashboard "Ask Agronomist" button)
  useEffect(() => {
    if (searchParams.get('openCase') === 'true' && field) {
      // Trigger the Ask Agronomist case button click
      const caseBtn = document.querySelector('[data-testid="agronomist-case-btn"]');
      if (caseBtn) {
        caseBtn.click();
        // Clear the URL parameter
        navigate(`/fields/${fieldId}`, { replace: true });
      }
    }
  }, [searchParams, field, fieldId, navigate]);

  useEffect(() => {
    // Only load alerts if field has active crop
    if (field && field.crop && field.sowing_date) {
      loadActiveAlerts();
    }
  }, [field]);

  const loadSeasonCosts = async () => {
    try {
      const response = await axios.get(`${API}/fields/${fieldId}/season-costs`, { headers: getAuthHeaders() });
      setSeasonCosts(response.data);
    } catch (error) {
      console.error('Failed to load season costs');
    }
  };

  // Load farm currency preference
  const loadFarmCurrency = async () => {
    try {
      const response = await axios.get(`${API}/farm`, { headers: getAuthHeaders() });
      if (response.data?.currency) {
        setFarmCurrency(response.data.currency);
        // Update activity form default currency
        setActivityForm(prev => ({ ...prev, currency: response.data.currency }));
      }
    } catch (error) {
      console.error('Failed to load farm currency');
    }
  };

  const loadActiveAlerts = async () => {
    try {
      // Load regular alerts
      const response = await axios.get(`${API}/fields/${fieldId}/alerts`, { headers: getAuthHeaders() });
      const regularAlerts = response.data || [];
      
      // Also load enhanced remote sensing alerts
      try {
        const rsResponse = await axios.get(`${API}/ndvi/${fieldId}/alerts/enhanced`, { headers: getAuthHeaders() });
        const rsAlerts = rsResponse.data?.alerts || [];
        
        // Combine, avoiding duplicates by alert_id
        const allAlerts = [...regularAlerts];
        for (const rsAlert of rsAlerts) {
          if (!allAlerts.some(a => a.alert_id === rsAlert.alert_id || a.id === rsAlert.id)) {
            allAlerts.push(rsAlert);
          }
        }
        
        // Sort by timestamp/created_at, newest first
        allAlerts.sort((a, b) => {
          const dateA = new Date(a.created_at || a.timestamp || 0);
          const dateB = new Date(b.created_at || b.timestamp || 0);
          return dateB - dateA;
        });
        
        setActiveAlerts(allAlerts);
      } catch (rsError) {
        // If RS alerts fail, just use regular alerts
        console.log('Could not load enhanced RS alerts:', rsError.message);
        setActiveAlerts(regularAlerts);
      }
    } catch (error) {
      console.error('Failed to load alerts');
    }
  };

  const handleCreateTaskFromAlert = async (alert) => {
    try {
      await createTaskFromAlert(alert, fieldId, field.farm_id, getAuthHeaders);
      toast.success('Task created from alert');
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleExportData = async (includePhotos = false) => {
    setExporting(true);
    setExportProgress(0);
    
    try {
      // Fetch all export data
      const response = await axios.get(`${API}/fields/${fieldId}/export-data`, { headers: getAuthHeaders() });
      const data = response.data;
      
      await exportSeasonData({
        field: data.field,
        activities: data.activities,
        scoutingPoints: data.scoutingPoints,
        tasks: data.tasks,
        alerts: data.alerts,
        weatherHistory: data.weatherHistory,
        summaries: data.summaries,
        ndviReadings: data.ndviReadings,
        cases: data.cases,
        caseMessages: data.caseMessages,
        includePhotos,
        onProgress: setExportProgress
      });
      
      toast.success('Export completed - check your downloads');
      setShowExportModal(false);
    } catch (error) {
      toast.error('Export failed');
      console.error('Export error:', error);
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  };

  const handleExportAndHarvest = async () => {
    if (!window.confirm('This will export all data and then DELETE all season data. Continue?')) return;
    
    await handleExportData(true);
    
    // After successful export, harvest
    try {
      await axios.post(`${API}/fields/${fieldId}/harvest-with-cleanup`, {}, { headers: getAuthHeaders() });
      toast.success('Field harvested - all season data deleted');
      loadFieldData();
    } catch (error) {
      toast.error('Harvest failed');
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await axios.post(`${API}/fields/${fieldId}/alerts/${encodeURIComponent(alertId)}/dismiss`, {}, { headers: getAuthHeaders() });
      setActiveAlerts(activeAlerts.filter(a => (a.id !== alertId && a.dedupe_key !== alertId)));
      toast.success('Alert dismissed');
    } catch (error) {
      toast.error('Failed to dismiss alert');
    }
  };

  const loadChatUsage = async () => {
    try {
      const response = await axios.get(`${API}/crop-health-scout/usage`, { headers: getAuthHeaders() });
      setChatUsage(response.data);
    } catch (error) {
      console.error('Failed to load chat usage');
    }
  };

  const loadSummaries = async () => {
    try {
      const response = await axios.get(`${API}/fields/${fieldId}/summaries`, { headers: getAuthHeaders() });
      setSummaries(response.data || []);
    } catch (error) {
      console.error('Failed to load summaries');
      setSummaries([]);
    }
  };

  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const response = await axios.post(`${API}/fields/${fieldId}/generate-summary`, {}, { headers: getAuthHeaders() });
      if (response.data.success) {
        toast.success('Weekly summary generated!');
        loadSummaries();
      } else {
        toast.info(response.data.message || 'Summary not generated');
      }
    } catch (error) {
      toast.error('Failed to generate summary');
    } finally {
      setGeneratingSummary(false);
    }
  };

  const handleDeleteSummary = async (summaryId) => {
    try {
      await axios.delete(`${API}/fields/${fieldId}/summaries/${summaryId}`, { headers: getAuthHeaders() });
      toast.success('Summary deleted');
      loadSummaries();
    } catch (error) {
      toast.error('Failed to delete summary');
    }
  };

  const loadFieldData = async () => {
    try {
      const response = await axios.get(`${API}/fields/${fieldId}`, { headers: getAuthHeaders() });
      setField(response.data);
      
      // Load activities only if field has crop
      if (response.data.crop) {
        const activitiesRes = await axios.get(`${API}/fields/${fieldId}/activities`, { headers: getAuthHeaders() });
        setActivities(activitiesRes.data || []);
      }
      
      // Load NDVI readings
      try {
        const ndviRes = await axios.get(`${API}/ndvi/${fieldId}`, { headers: getAuthHeaders() });
        setNdviReadings(ndviRes.data?.readings || []);
      } catch (e) {
        setNdviReadings([]);
      }
    } catch (error) {
      toast.error('Failed to load field data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddActivity = async () => {
    if (!activityForm.activity_type) {
      toast.error('Please select an activity type');
      return;
    }
    
    try {
      await axios.post(`${API}/fields/${fieldId}/activities`, activityForm, { headers: getAuthHeaders() });
      setShowAddActivity(false);
      setActivityForm({ 
        activity_type: '', 
        date: new Date().toISOString().split('T')[0], 
        notes: '',
        productName: '',
        quantityPerHa: '',
        quantityUnit: '',
        costPerHa: '',
        currency: farmCurrency // Reset to farm default currency
      });
      loadFieldData();
      loadSeasonCosts();
      toast.success('Activity logged!');
    } catch (error) {
      toast.error('Failed to add activity');
    }
  };

  const handleHarvest = async () => {
    if (!window.confirm(
      'Are you sure you want to harvest this field?\n\n' +
      'This will PERMANENTLY delete:\n' +
      '- All weather monitoring data\n' +
      '- All NDVI/vegetation data\n' +
      '- All alerts and activities\n' +
      '- Crop and sowing date information\n\n' +
      'Only the field polygon and harvest date will be kept.\n' +
      'You will need to add a new crop to start monitoring again.'
    )) return;
    
    try {
      const response = await axios.post(`${API}/fields/${fieldId}/harvest`, {}, { headers: getAuthHeaders() });
      toast.success(`Field harvested on ${response.data.harvest_date}!`);
      loadFieldData(); // Reload to show harvested state
    } catch (error) {
      toast.error('Failed to harvest field');
    }
  };

  const handleStartNewCrop = async () => {
    if (!newCropForm.crop || !newCropForm.sowing_date) {
      toast.error('Please select crop type and sowing date');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/fields/${fieldId}/new-crop`,
        newCropForm,
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message);
      setShowNewCropModal(false);
      setNewCropForm({ crop: '', sowing_date: new Date().toISOString().split('T')[0] });
      loadFieldData(); // Reload to show new crop cycle
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start new crop cycle');
    }
  };

  const isHarvested = field && (!field.crop || field.stage === 'harvested' || field.stage === 'inactive');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-pulse text-lg">Loading field details...</div>
        </div>
      </Layout>
    );
  }

  if (!field) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-xl">Field not found</p>
        </div>
      </Layout>
    );
  }

  // Harvested State UI
  if (isHarvested) {
    return (
      <Layout>
        <div className="space-y-6" data-testid="field-details-harvested">
          {/* Header */}
          <div className="section-header">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">{field.name}</h1>
              <p className="mt-2">
                <span className="badge badge-warning">
                  <Sprout className="w-4 h-4" />
                  Harvested - No Active Crop
                </span>
              </p>
            </div>
            <button
              onClick={() => setShowNewCropModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="add-new-crop-button"
            >
              <Plus className="w-5 h-5" />
              Add New Crop
            </button>
          </div>

          {/* Harvest Info Card */}
          <div className="card p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="w-6 h-6 text-warning" />
                  <span className="stat-label">Last Harvest Date</span>
                </div>
                <p className="stat-value text-warning">
                  {field.harvest_date ? new Date(field.harvest_date).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Sprout className="w-6 h-6 text-muted-foreground" />
                  <span className="stat-label">Status</span>
                </div>
                <p className="text-xl font-medium text-muted-foreground">
                  Ready for New Planting
                </p>
              </div>
            </div>
          </div>

          {/* Field Map Preview */}
          <div className="card p-6">
            <h2 className="section-title mb-4">Field Boundary</h2>
            <div className="bg-muted rounded-lg p-8 text-center">
              <p className="text-muted-foreground">
                Field polygon preserved. Add a new crop to resume monitoring.
              </p>
              {field.geometry && (
                <p className="text-sm mt-2 text-muted-foreground">
                  {field.geometry.coordinates?.[0]?.length || 0} boundary points defined
                </p>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="alert alert-info">
            <div className="flex-1">
              <h3 className="font-semibold mb-2">Start New Growing Cycle</h3>
              <p className="mb-4">
                Click "Add New Crop" to select a crop type and sowing date. 
                This will restart all monitoring systems including:
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li>Weather monitoring & GDD tracking</li>
                <li>NDVI vegetation health analysis</li>
                <li>Disease & pest risk alerts</li>
                <li>Growth stage tracking</li>
              </ul>
            </div>
          </div>

          {/* New Crop Modal */}
          {showNewCropModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in" onClick={() => setShowNewCropModal(false)}>
              <div className="card p-8 w-full max-w-md mx-4 animate-scale-in" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-2xl font-semibold mb-6 text-foreground">Start New Crop Cycle</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="input-label">Crop Type *</label>
                    <select
                      value={newCropForm.crop}
                      onChange={(e) => setNewCropForm({ ...newCropForm, crop: e.target.value })}
                      className="input-field"
                      data-testid="new-crop-select"
                    >
                      <option value="">Select crop type</option>
                      {CROP_TYPES.map(crop => (
                        <option key={crop.value} value={crop.value}>{crop.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="input-label">Sowing Date *</label>
                    <input
                      type="date"
                      value={newCropForm.sowing_date}
                      onChange={(e) => setNewCropForm({ ...newCropForm, sowing_date: e.target.value })}
                      className="input-field"
                      data-testid="new-crop-date"
                    />
                  </div>
                </div>
                
                <div className="flex gap-4 mt-6">
                  <button
                    onClick={handleStartNewCrop}
                    className="btn-primary flex-1"
                    data-testid="confirm-new-crop-button"
                  >
                    Start Monitoring
                  </button>
                  <button
                    onClick={() => setShowNewCropModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // Active Crop State UI (existing implementation)
  return (
    <Layout>
      <div className="space-y-6" data-testid="field-details">
        {/* Header */}
        <div className="section-header">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground">{field.name}</h1>
            <div className="flex items-center gap-4 mt-1">
              <p className="text-muted-foreground">Crop: {field.crop}</p>
              {field.areaHa > 0 && (
                <span className="px-2 py-0.5 bg-primary/10 text-primary text-sm rounded">
                  {field.areaHa?.toFixed(2)} ha
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <AgronomistCaseButton fieldId={fieldId} fieldName={field.name} />
            <button
              onClick={() => setShowExportModal(true)}
              className="btn-depth-secondary flex items-center gap-2"
              data-testid="export-button"
            >
              <Download className="w-5 h-5" />
              Export
            </button>
            <button
              onClick={() => setShowNDVI(true)}
              className="btn-depth-primary flex items-center gap-2"
              data-testid="vegetation-button"
            >
              <Leaf className="w-5 h-5" />
              Vegetation
            </button>
            <button
              onClick={handleHarvest}
              className="btn-depth-danger flex items-center gap-2"
              data-testid="harvest-button"
            >
              Harvest Field
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-6 h-6 text-accent" />
              <span className="stat-label">GDD</span>
            </div>
            <p className="stat-value text-accent">{field.gdd?.toFixed(1) || 0}</p>
          </div>
          
          <div className="stat-card">
            <div className="flex items-center gap-3 mb-2">
              <CloudRain className="w-6 h-6 text-info" />
              <span className="stat-label">Rain</span>
            </div>
            <p className="stat-value text-info">{field.rain_accumulation?.toFixed(1) || 0} mm</p>
            {field.sowing_date && (
              <p className="text-xs" style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                Since: {new Date(field.sowing_date).toLocaleDateString()}
              </p>
            )}
          </div>
          
          <div className="stat-card">
            <div className="flex items-center gap-3 mb-2">
              <Sprout className="w-6 h-6 text-success" />
              <span className="stat-label">Sowing</span>
            </div>
            <p className="text-lg font-semibold text-foreground">{field.sowing_date ? new Date(field.sowing_date).toLocaleDateString() : 'N/A'}</p>
          </div>
          
          <div className="stat-card">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-6 h-6 text-muted-foreground" />
              <span className="stat-label">Stage</span>
            </div>
            <p className="text-lg font-semibold capitalize text-foreground">{field.stage || 'Unknown'}</p>
          </div>

          {/* Season Cost Summary (Feature 3) - A) With Currency Display */}
          <div className="stat-card">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-6 h-6 text-warning" />
              <span className="stat-label">Season Cost</span>
            </div>
            {seasonCosts.byCurrency && Object.keys(seasonCosts.byCurrency).length > 1 ? (
              // Mixed currencies - show breakdown
              <div className="space-y-1">
                {Object.entries(seasonCosts.byCurrency).map(([currency, amount]) => (
                  <p key={currency} className="text-sm font-semibold text-warning">
                    {amount?.toFixed(0) || 0} {currency === 'EUR' ? '€' : '$'}
                  </p>
                ))}
              </div>
            ) : (
              // Single currency
              <p className="text-lg font-semibold text-warning">
                {seasonCosts.totalCost?.toFixed(0) || 0} {seasonCosts.currency === 'USD' ? '$' : '€'}
                {field.areaHa > 0 && (
                  <span className="text-sm font-normal text-muted-foreground ml-1">
                    ({seasonCosts.costPerHa?.toFixed(0) || 0}/ha)
                  </span>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="flex gap-2 border-b border-border overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: Activity },
            { id: 'scouting', label: 'Scouting', icon: Target },
            { id: 'tasks', label: 'Tasks', icon: CheckSquare },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 border-b-2 transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <>
            {/* Active Alerts */}
            {activeAlerts.length > 0 && (
              <div className="card p-6">
                <h2 className="section-title mb-4">
                  <AlertCircle className="w-5 h-5 text-warning" />
                  Active Alerts ({activeAlerts.length})
                </h2>
                <div className="space-y-3">
                  {activeAlerts.slice(0, 5).map((alert, index) => {
                    const isRemoteSensing = alert.type === 'remote_sensing';
                    const severityColor = alert.severity === 'high' ? 'danger' : 
                                         alert.severity === 'medium' ? 'warning' : 'primary';
                    
                    return (
                      <div 
                        key={index} 
                        className={`card p-4 cursor-pointer hover:bg-muted/50 transition border-l-4`}
                        style={{ 
                          borderLeftColor: severityColor === 'danger' ? 'var(--danger)' : 
                                          severityColor === 'warning' ? 'var(--warning)' : 'var(--primary)'
                        }}
                        onClick={() => setSelectedAlert(alert)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className={`badge badge-${severityColor} text-xs`}>
                                {isRemoteSensing ? alert.subtype?.replace(/_/g, ' ') : alert.type}
                              </span>
                              {alert.severity && (
                                <span className="text-xs text-muted-foreground">
                                  {alert.severity}
                                </span>
                              )}
                              {isRemoteSensing && alert.metrics?.index && (
                                <span className="text-xs font-mono bg-muted px-1 rounded">
                                  {alert.metrics.index}
                                </span>
                              )}
                            </div>
                            <span className="font-medium text-foreground">{alert.title}</span>
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {alert.summary || (Array.isArray(alert.why) ? alert.why[0] : alert.why)}
                            </p>
                            {/* Quick metrics for remote sensing */}
                            {isRemoteSensing && alert.metrics && (
                              <div className="flex items-center gap-4 mt-2 text-xs">
                                <span className="text-muted-foreground">
                                  Current: <span className="font-mono">{alert.metrics.current?.toFixed(2)}</span>
                                </span>
                                {alert.metrics.delta && (
                                  <span className={alert.metrics.delta < 0 ? 'text-danger' : 'text-success'}>
                                    Δ {alert.metrics.delta > 0 ? '+' : ''}{alert.metrics.delta?.toFixed(2)} 
                                    ({alert.metrics.pct_delta?.toFixed(0)}%)
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button 
                              onClick={(e) => { e.stopPropagation(); handleCreateTaskFromAlert(alert); }} 
                              className="text-primary hover:text-primary/80 p-1"
                              title="Create task"
                            >
                              <Clipboard className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={(e) => { e.stopPropagation(); handleDeleteAlert(alert.alert_id || alert.id); }} 
                              className="text-muted-foreground hover:text-danger p-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

        {/* Alerts & Recommendations */}
        {field.crop && (
          <div className="card p-6">
            <div className="section-header mb-4">
              <h2 className="section-title">
                <AlertCircle className="w-5 h-5" />
                Field Alerts & Recommendations
              </h2>
              <button
                onClick={() => setShowAlerts(!showAlerts)}
                className="text-sm text-muted-foreground hover:underline"
              >
                {showAlerts ? 'Hide' : 'Show'}
              </button>
            </div>
            {showAlerts && <AlertsPanel fieldId={fieldId} />}
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Crop Health Scout */}
          <div className="card p-6">
            <h2 className="section-title mb-4">
              <MessageSquare className="w-5 h-5" />
              Crop Health Scout
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {chatUsage.remaining}/{chatUsage.limit} messages remaining today
            </p>
            <button
              onClick={() => setShowCropHealthScout(true)}
              disabled={chatUsage.remaining === 0}
              className="btn-primary w-full disabled:opacity-50"
            >
              Ask AI Scout
            </button>
          </div>

          {/* Vegetation (NDVI) */}
          <div className="card p-6">
            <h2 className="section-title mb-4">
              <Leaf className="w-5 h-5 text-success" />
              Vegetation (NDVI)
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              {ndviReadings.length} readings from Sentinel-2
            </p>
            <button
              onClick={() => setShowNDVI(true)}
              className="btn-secondary w-full"
            >
              View NDVI Map
            </button>
          </div>
        </div>

        {/* Field Activities */}
        <div className="card p-6">
          <div className="section-header mb-4">
            <h2 className="section-title">
              <Activity className="w-5 h-5" />
              Field Activities
            </h2>
            <button
              onClick={() => setShowAddActivity(true)}
              className="btn-secondary text-sm"
            >
              + Log Activity
            </button>
          </div>
          
          {activities.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No activities logged yet</p>
          ) : (
            <div className="space-y-2">
              {activities.slice(0, 5).map((activity, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <span className="font-medium capitalize text-foreground">{activity.activity_type?.replace('_', ' ')}</span>
                    {activity.notes && <p className="text-sm text-muted-foreground">{activity.notes}</p>}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {new Date(activity.date).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Weekly AI Summary (D) */}
        <div className="card p-6">
          <div className="section-header mb-4">
            <h2 className="section-title">
              <FileText className="w-5 h-5" />
              Weekly AI Summary
            </h2>
            <button
              onClick={handleGenerateSummary}
              disabled={generatingSummary || field.stage === 'inactive' || field.stage === 'harvested'}
              className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50"
              data-testid="generate-summary-btn"
            >
              {generatingSummary ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Generate Now
                </>
              )}
            </button>
          </div>
          
          {field.stage === 'inactive' || field.stage === 'harvested' ? (
            <p className="text-muted-foreground text-center py-4">
              Monitoring inactive - no summaries available
            </p>
          ) : summaries.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              No summaries yet. Generate one or wait for auto-generation (every 5 days).
            </p>
          ) : (
            <div className="space-y-4">
              {summaries.map((summary, index) => (
                <div key={summary.id} className="card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">
                      {new Date(summary.created_at).toLocaleDateString()} {summary.auto_generated && '(auto)'}
                    </span>
                  </div>
                  <div 
                    className="prose prose-sm max-w-none text-sm text-foreground"
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {summary.content?.substring(0, 500)}
                    {summary.content?.length > 500 && (
                      <button 
                        onClick={() => setShowSummaries(summary.id)}
                        className="text-primary hover:underline ml-1"
                      >
                        ...read more
                      </button>
                    )}
                  </div>
                  {summary.content?.length <= 500 && (
                    <button 
                      onClick={() => setShowSummaries(summary.id)}
                      className="text-primary hover:underline text-sm mt-2"
                    >
                      View Full Summary
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
          </>
        )}

        {/* Scouting Tab Content */}
        {activeTab === 'scouting' && (
          <div className="card p-6">
            <ScoutingPanel 
              fieldId={fieldId} 
              farmId={field.farm_id}
              onPointCreated={() => {}}
              onOpenMap={(scoutModeEnabled) => {
                setShowNDVI(true);
                // The NDVI panel will handle scout mode
              }}
            />
          </div>
        )}

        {/* Tasks Tab Content */}
        {activeTab === 'tasks' && (
          <div className="card p-6">
            <TasksPanel 
              farmId={field.farm_id} 
              fieldId={fieldId}
            />
          </div>
        )}

        {/* Full Summary Modal */}
        {showSummaries && (
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 animate-fade-in" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }} onClick={() => setShowSummaries(false)}>
            <div className="card-floating p-6 w-full max-w-3xl max-h-[80vh] overflow-y-auto animate-scale-in" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold" style={{ color: 'var(--text)' }}>Weekly AI Summary</h2>
                <button onClick={() => setShowSummaries(false)} className="p-2 hover:bg-muted rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 rounded-xl" style={{ background: 'var(--card-cream, rgba(255,255,255,0.05))', border: '1px solid var(--border-soft)' }}>
                <div style={{ whiteSpace: 'pre-wrap', color: 'var(--text)', fontSize: 14, lineHeight: 1.7 }}>
                  {summaries.find(s => s.id === showSummaries)?.content || 'Summary content not available'}
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button onClick={() => setShowSummaries(false)} className="btn-depth-secondary">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* NDVI Visualization Modal */}
        {showNDVI && (
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 animate-fade-in" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
            <div className="card-floating w-full max-w-6xl p-6 max-h-[90vh] overflow-y-auto animate-scale-in">
              <NDVIVisualization
                field={field}
                ndviReadings={ndviReadings}
                onClose={() => setShowNDVI(false)}
                onRefresh={loadFieldData}
              />
            </div>
          </div>
        )}

        {/* Add Activity Modal */}
        {showAddActivity && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in" onClick={() => setShowAddActivity(false)}>
            <div className="card p-8 w-full max-w-md mx-4 animate-scale-in" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-2xl font-semibold mb-6 text-foreground">Log Activity</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="input-label">Activity Type</label>
                  <select
                    value={activityForm.activity_type}
                    onChange={(e) => setActivityForm({ ...activityForm, activity_type: e.target.value })}
                    className="input-field"
                  >
                    <option value="">Select activity</option>
                    <option value="scouting">Field Scouting</option>
                    <option value="spraying">Spraying</option>
                    <option value="fertilizing">Fertilizing</option>
                    <option value="irrigation">Irrigation</option>
                    <option value="tillage">Tillage</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="input-label">Date</label>
                  <input
                    type="date"
                    value={activityForm.date}
                    onChange={(e) => setActivityForm({ ...activityForm, date: e.target.value })}
                    className="input-field"
                  />
                </div>
                
                <div>
                  <label className="input-label">Notes</label>
                  <textarea
                    value={activityForm.notes}
                    onChange={(e) => setActivityForm({ ...activityForm, notes: e.target.value })}
                    className="input-field"
                    rows={2}
                    placeholder="Optional notes..."
                  />
                </div>

                {/* Cost Tracking Fields (Feature 3) */}
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium mb-3 text-foreground">Cost Tracking (optional)</p>
                  
                  <div>
                    <label className="input-label">Product Name</label>
                    <input
                      type="text"
                      value={activityForm.productName}
                      onChange={(e) => setActivityForm({ ...activityForm, productName: e.target.value })}
                      className="input-field"
                      placeholder="e.g., Herbicide XYZ"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="input-label">Rate per ha</label>
                      <input
                        type="number"
                        step="0.1"
                        value={activityForm.quantityPerHa}
                        onChange={(e) => setActivityForm({ ...activityForm, quantityPerHa: e.target.value })}
                        className="input-field"
                        placeholder="2.5"
                      />
                    </div>
                    <div>
                      <label className="input-label">Unit</label>
                      <select
                        value={activityForm.quantityUnit}
                        onChange={(e) => setActivityForm({ ...activityForm, quantityUnit: e.target.value })}
                        className="input-field"
                      >
                        <option value="">Select</option>
                        <option value="kg/ha">kg/ha</option>
                        <option value="l/ha">l/ha</option>
                        <option value="pcs/ha">pcs/ha</option>
                        <option value="units/ha">units/ha</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <label className="input-label">Cost per ha</label>
                    <div className="flex items-center gap-1">
                      {/* Currency symbol - minimal width */}
                      <span className="text-muted-foreground text-sm" style={{ minWidth: 20 }}>
                        {activityForm.currency === 'EUR' ? '€' : '$'}
                      </span>
                      {/* Value input - takes most space */}
                      <input
                        type="number"
                        step="0.01"
                        value={activityForm.costPerHa}
                        onChange={(e) => setActivityForm({ ...activityForm, costPerHa: e.target.value })}
                        className="input-field"
                        style={{ flex: 1, minWidth: 140 }}
                        placeholder="45.00"
                      />
                      {/* Currency selector - compact */}
                      <select
                        value={activityForm.currency}
                        onChange={(e) => setActivityForm({ ...activityForm, currency: e.target.value })}
                        className="input-field"
                        style={{ width: 70, padding: '8px 4px', fontSize: 12 }}
                      >
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                    {activityForm.costPerHa && field.areaHa > 0 && (
                      <p className="text-sm text-success mt-1">
                        Total: {(parseFloat(activityForm.costPerHa) * field.areaHa).toFixed(2)} {activityForm.currency === 'EUR' ? '€' : '$'} ({field.areaHa?.toFixed(2)} ha)
                      </p>
                    )}
                    {activityForm.costPerHa && !field.areaHa && (
                      <p className="text-sm text-warning mt-1">
                        Field area unknown - cannot calculate total
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-4 mt-6">
                <button onClick={handleAddActivity} className="btn-primary flex-1">Save</button>
                <button onClick={() => setShowAddActivity(false)} className="btn-secondary flex-1">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Crop Health Scout Modal */}
        {showCropHealthScout && (
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 animate-fade-in" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }} onClick={() => setShowCropHealthScout(false)}>
            <div className="card p-6 w-full max-w-xl mx-4 max-h-[85vh] flex flex-col animate-scale-in" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-xl font-semibold mb-4 flex-shrink-0 text-foreground">Crop Health Scout AI</h2>
              
              {/* Photo preview */}
              {scoutImagePreview && (
                <div className="mb-3 relative inline-block">
                  <img src={scoutImagePreview} alt="Upload preview" className="h-20 rounded-lg border border-border" />
                  <button
                    onClick={() => { setScoutImage(null); setScoutImagePreview(null); }}
                    className="absolute -top-2 -right-2 p-1 bg-danger rounded-full"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              )}
              
              <div className="flex-shrink-0 mb-4">
                <textarea
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  className="input-field text-sm"
                  rows={3}
                  placeholder="Describe your crop issue or ask a question..."
                />
              </div>
              
              <div className="flex gap-2 mb-4">
                {/* Photo upload button */}
                <label className="btn-secondary flex items-center gap-2 cursor-pointer flex-shrink-0">
                  <Camera className="w-4 h-4" />
                  Photo
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error('Image must be less than 5MB');
                          return;
                        }
                        setScoutImage(file);
                        setScoutImagePreview(URL.createObjectURL(file));
                      }
                    }}
                  />
                </label>
                
                <button
                  onClick={async () => {
                    if (!chatMessage.trim() && !scoutImage) return;
                    try {
                      let imageUrl = null;
                      
                      // Upload image first if present
                      if (scoutImage) {
                        const formData = new FormData();
                        formData.append('file', scoutImage);
                        const uploadRes = await axios.post(`${API}/upload-image`, formData, {
                          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
                        });
                        imageUrl = uploadRes.data.url;
                      }
                      
                      const response = await axios.post(`${API}/crop-health-scout/chat`, {
                        message: chatMessage,
                        field_id: fieldId,
                        image_urls: imageUrl ? [imageUrl] : []
                      }, { headers: getAuthHeaders() });
                      setChatResponse(response.data.response);
                      setChatMessage('');
                      setScoutImage(null);
                      setScoutImagePreview(null);
                      loadChatUsage();
                    } catch (error) {
                      if (error.response?.status === 429) {
                        toast.error(error.response.data.detail);
                      } else {
                        toast.error('Failed to get response');
                      }
                    }
                  }}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Send Message
                </button>
              </div>
              
              {chatResponse && (
                <div className="flex-1 overflow-y-auto alert alert-success mb-4">
                  <p className="whitespace-pre-wrap text-sm">{chatResponse}</p>
                </div>
              )}
              
              <button
                onClick={() => setShowCropHealthScout(false)}
                className="btn-secondary w-full flex-shrink-0"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Export Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in" onClick={() => !exporting && setShowExportModal(false)}>
            <div className="card p-6 w-full max-w-md animate-scale-in" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-xl font-semibold mb-4 text-foreground">Export Season Data</h2>
              
              {exporting ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
                  <p className="text-muted-foreground mb-2">Exporting data...</p>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all"
                      style={{ width: `${exportProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{exportProgress}%</p>
                </div>
              ) : (
                <>
                  <p className="text-muted-foreground mb-6">
                    Export all season data including activities, scouting points, tasks, alerts, and weather history as a ZIP file.
                  </p>
                  
                  <div className="space-y-3">
                    <button
                      onClick={() => handleExportData(false)}
                      className="btn-secondary w-full flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Export without photos
                    </button>
                    
                    <button
                      onClick={() => handleExportData(true)}
                      className="btn-primary w-full flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Export with photos
                    </button>
                    
                    <hr className="my-4" />
                    
                    <button
                      onClick={handleExportAndHarvest}
                      className="btn-danger w-full flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Export & Harvest
                    </button>
                    <p className="text-xs text-muted-foreground text-center">
                      This will export all data and then delete all season records
                    </p>
                  </div>
                  
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="btn-secondary w-full mt-4"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Alert Detail Modal */}
        {selectedAlert && (
          <AlertDetailModal
            alert={selectedAlert}
            onClose={() => setSelectedAlert(null)}
            onDismiss={async (alertId) => {
              setDismissingAlert(true);
              try {
                await handleDeleteAlert(alertId);
                setSelectedAlert(null);
              } finally {
                setDismissingAlert(false);
              }
            }}
            dismissing={dismissingAlert}
          />
        )}
      </div>
    </Layout>
  );
};

export default FieldDetails;
